import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Row, Spinner } from 'reactstrap';
import Alert from '@/shared/components/Alert';
import { postLastMonthsUsageFee } from '../qaToolsApi';

const UNAVAILABLE = 'QA Tools are not available on this environment.';

const money = amount => `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Why: a month's usage fee is posted in the month after it, and a re-run or recovery posts it
 *      later still; this posts one program's last-month fee now so a tester can see what a late
 *      fee does to the Month-to-Date Invoice Preview.
 * Scope: one program at a time (the API answers 404 outside QA and local); says what was posted
 *        and links to the program whose preview shows it.
 * SRP: takes the program, triggers the post and shows the outcome; posting is the API's job.
 */
const PostLastMonthsUsageFee = () => {
  const [programId, setProgramId] = useState('');
  const [posted, setPosted] = useState(null);
  const [error, setError] = useState(null);
  const [isBusy, setBusy] = useState(false);

  const onPost = async () => {
    setBusy(true);
    setError(null);
    setPosted(null);
    const result = await postLastMonthsUsageFee(Number(programId));
    setBusy(false);
    if (result.available === false) {
      setError(UNAVAILABLE);
      return;
    }
    if (result.errors) {
      setError(result.errors);
      return;
    }
    setPosted(result.posted);
  };

  return (
    <>
      <div className='card__title'>
        <h5 className='subhead'>
          Posts a program's usage fee for last month now, the way a re-run or a recovery posts it late.
          Press it twice to post it twice. Then open the program's Month-to-Date Invoice Preview under Billing.
          Use it only after last month has been invoiced: before that, the monthly run would skip the program's real fee.
          The fee it posts is not added to any invoice.
        </h5>
      </div>
      <Row className='mb-3'>
        <Col md={3}>
          <label htmlFor='qa-usage-fee-program'>Program ID</label>
          <input id='qa-usage-fee-program' className='form-control' type='number' min='1'
            value={programId} onChange={event => setProgramId(event.target.value)} />
        </Col>
        <Col md={4} className='d-flex align-items-end'>
          <Button color='primary' size='sm' className='mb-0' disabled={isBusy || !programId} onClick={onPost}>
            {isBusy ? <><Spinner size='sm' className='mr-2' />Posting...</> : "Post last month's usage fee"}
          </Button>
        </Col>
      </Row>

      {error && <Alert color='danger' className='alert--bordered' icon><p>{error}</p></Alert>}
      {posted && (
        <p>
          {`Posted the ${posted.period} usage fee for ${posted.program_name}: ${posted.rate} × ${money(posted.base)} = ${money(posted.fee)}, at ${posted.posted_at} UTC. `}
          See it on the Month-to-Date Invoice Preview of{' '}
          <Link to={`/program/view/${posted.preview_program_id}`}>{posted.preview_program_name}</Link>.
        </p>
      )}
    </>
  );
};

export default PostLastMonthsUsageFee;
